import { useEffect, useRef } from "react";
import { isGlobalUploadSuppressed } from "@/features/globalUpload/globalUploadSuppress";

function isFileDrag(event: DragEvent): boolean {
  const types = event?.dataTransfer?.types;
  if (!types) return false;
  return Array.from(types).includes("Files");
}

function isInternalBuilderDragActive(): boolean {
  return (window as Window & { __ehInternalBuilderDrag?: boolean }).__ehInternalBuilderDrag === true;
}

function isBuilderDragContext(event: DragEvent): boolean {
  const path = typeof event.composedPath === "function" ? event.composedPath() : [];
  const inPath = path.some(
    (node) =>
      node instanceof HTMLElement &&
      (node.id === "maintenanceBuilderCanvas" || node.id === "maintenanceTemplatePalette"),
  );
  if (inPath) return true;

  const target = event.target;
  if (!(target instanceof Element)) return false;
  return Boolean(target.closest("#maintenanceBuilderCanvas, #maintenanceTemplatePalette"));
}

function isProjectFotoDropTarget(event: DragEvent): boolean {
  const path = typeof event.composedPath === "function" ? event.composedPath() : [];
  if (
    path.some(
      (node) => node instanceof HTMLElement && node.hasAttribute("data-project-foto-drop"),
    )
  ) {
    return true;
  }
  const target = event.target;
  return target instanceof Element && Boolean(target.closest("[data-project-foto-drop]"));
}

/** Drop-Zonen, die die Datei selbst verarbeiten und die Dokumentablage nicht öffnen sollen. */
function isOwnFileDropTarget(event: DragEvent): boolean {
  const target = event.target;
  return (
    target instanceof Element &&
    Boolean(target.closest("[data-werkszeugnis-drop], [data-maintenance-plan-drop]"))
  );
}

function shouldSkipGlobalUpload(event: DragEvent): boolean {
  return (
    isGlobalUploadSuppressed() ||
    isInternalBuilderDragActive() ||
    (isBuilderDragContext(event) && !isFileDrag(event))
  );
}

/** True when the drag pointer moves across the document boundary (in or out). */
function isCrossingDocumentBoundary(relatedTarget: EventTarget | null): boolean {
  if (relatedTarget == null) return true;
  return relatedTarget instanceof Node && !document.documentElement.contains(relatedTarget);
}

export function useGlobalFileDrop({
  enabled,
  onDrop,
  onDragActiveChange,
}: {
  enabled: boolean;
  onDrop: (files: FileList) => void;
  onDragActiveChange: (active: boolean) => void;
}) {
  const dragCounterRef = useRef(0);
  const onDropRef = useRef(onDrop);
  const onDragActiveChangeRef = useRef(onDragActiveChange);

  onDropRef.current = onDrop;
  onDragActiveChangeRef.current = onDragActiveChange;

  useEffect(() => {
    if (!enabled) {
      dragCounterRef.current = 0;
      onDragActiveChangeRef.current(false);
      return;
    }

    function handleDragOver(ev: DragEvent) {
      if (!isFileDrag(ev)) return;
      if (shouldSkipGlobalUpload(ev)) {
        // Unterdrückter Upload: Browser soll die Datei nicht öffnen, Overlay bleibt aus.
        // Eigene Drop-Zonen setzen den Cursor selbst und fangen den Drop ab.
        if (isGlobalUploadSuppressed() && !isOwnFileDropTarget(ev)) {
          ev.preventDefault();
          if (ev.dataTransfer) ev.dataTransfer.dropEffect = "none";
        }
        return;
      }
      ev.preventDefault();
      if (isProjectFotoDropTarget(ev)) {
        onDragActiveChangeRef.current(false);
      } else if (dragCounterRef.current > 0) {
        onDragActiveChangeRef.current(true);
      }
    }

    function deactivateDrag() {
      dragCounterRef.current = 0;
      onDragActiveChangeRef.current(false);
    }

    function handleDragEnter(ev: DragEvent) {
      if (!isFileDrag(ev) || shouldSkipGlobalUpload(ev)) return;
      ev.preventDefault();
      if (!isCrossingDocumentBoundary(ev.relatedTarget)) return;
      dragCounterRef.current += 1;
      onDragActiveChangeRef.current(true);
    }

    function handleDragLeave(ev: DragEvent) {
      if (!isFileDrag(ev) || shouldSkipGlobalUpload(ev)) return;
      if (!isCrossingDocumentBoundary(ev.relatedTarget)) return;
      dragCounterRef.current = Math.max(0, dragCounterRef.current - 1);
      if (dragCounterRef.current === 0) {
        onDragActiveChangeRef.current(false);
      }
    }

    function handleDrop(ev: DragEvent) {
      if (!isFileDrag(ev)) return;
      if (isOwnFileDropTarget(ev)) {
        deactivateDrag();
        return;
      }
      if (shouldSkipGlobalUpload(ev)) {
        if (isGlobalUploadSuppressed()) {
          ev.preventDefault();
          deactivateDrag();
        }
        return;
      }
      // Fotos auf der Fotos-Kachel stoppt deren Listener. Alles andere öffnet die Dokumentablage.
      ev.preventDefault();
      deactivateDrag();
      if (ev.dataTransfer?.files?.length) {
        onDropRef.current(ev.dataTransfer.files);
      }
    }

    function handleDragEnd() {
      deactivateDrag();
    }

    function handleVisibilityChange() {
      if (document.visibilityState === "hidden") {
        deactivateDrag();
      }
    }

    document.addEventListener("dragover", handleDragOver);
    document.addEventListener("dragenter", handleDragEnter);
    document.addEventListener("dragleave", handleDragLeave);
    document.addEventListener("drop", handleDrop);
    window.addEventListener("dragend", handleDragEnd);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      dragCounterRef.current = 0;
      document.removeEventListener("dragover", handleDragOver);
      document.removeEventListener("dragenter", handleDragEnter);
      document.removeEventListener("dragleave", handleDragLeave);
      document.removeEventListener("drop", handleDrop);
      window.removeEventListener("dragend", handleDragEnd);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      onDragActiveChangeRef.current(false);
    };
  }, [enabled]);
}
